import { api, tokens } from "./api";

// Same base as the API client: empty in development (the Vite proxy forwards /api) and in production
// (nginx does), so the browser only ever talks to its own origin.
const BASE = import.meta.env.VITE_API_BASE ?? "";

// Set only for a session that came from "Sign in with SSO". Logout needs it to know that the proxy
// session (and Zitadel's) must be ended too.
const AUTH_MODE_KEY = "ebmr.auth_mode";
const AUTH_MODE_SSO = "sso";
// After a refused SSO sign-in, Zitadel would silently reuse the same account on the next click. Remember
// to ask it to ask for credentials again instead.
const SSO_PICK_ACCOUNT = "ebmr.sso_pick_account";
// Refusals after which a different account is what the person needs.
const REFUSED_CODES = ["sso_user_not_provisioned", "account_inactive"];

export const isSsoSession = () => localStorage.getItem(AUTH_MODE_KEY) === AUTH_MODE_SSO;

export async function login(username, password) {
  const data = await api.post("/api/login", { username, password });
  tokens.set({ access: data.access, refresh: data.refresh });
  // Everything /api/me returns, minus the tokens — which are held by
  // `tokens`, not by the identity in the store. Picking fields by hand here
  // silently dropped `permissions`, so the sider rendered ungated until a
  // reload ran restoreSession() and fetched the identity properly.
  const { access: _a, refresh: _r, ...identity } = data;
  return identity;
}

/**
 * Signs out. Returns the address the browser must go to next (Zitadel's end-session URL) when this
 * was an SSO sign-in, otherwise null. An SSO sign-out that stopped at forgetting the tokens would let
 * the next click on "Sign in with SSO" sign the same person straight back in.
 */
export async function logout() {
  const sso = isSsoSession();
  let nextUrl = null;
  try {
    const result = await api.post("/api/logout", sso ? { sso: true } : undefined);
    nextUrl = result?.sso_logout_url ?? null;
  } finally {
    // Tokens are stateless, so signing out is the client discarding them —
    // do it even if the courtesy call to the server fails.
    tokens.clear();
    localStorage.removeItem(AUTH_MODE_KEY);
  }
  return nextUrl;
}

export const me = () => api.get("/api/me");

// ===================== Single sign-on =====================
// "Sign in with SSO" goes through the central system and its auth proxy (Zitadel). The proxy sends the
// browser back to this app with ?login=success and its own session cookie; completeSso() exchanges that
// for the usual Planner tokens, after which the app loads exactly as after a password sign-in.
// Plain fetch on purpose: these run before there is a session, so the client's refresh-and-retry
// must not get involved.

// What the auth proxy puts in `?error=` when a sign-in is turned back before the Planner is reached.
// A message that is already a sentence (for example "User Not Found, please contact admin") passes through.
const SSO_ERROR_MESSAGES = {
  sso_disabled: "Single sign-on is switched off for this system.",
  token_exchange_failed: "Single sign-on could not be completed. Please try again.",
  missing_code: "Single sign-on could not be completed. Please try again.",
  application_lookup_failed:
    "This application is not registered for single sign-on. Contact your administrator.",
  access_denied: "Sign-in was cancelled or not allowed.",
};

export const ssoErrorMessage = (code) => SSO_ERROR_MESSAGES[code] ?? code;

/**
 * The proxy turned this person back. Zitadel still holds their session, so the next click on "Sign in
 * with SSO" would sign the same person straight back in and fail the same way: ask for the account
 * picker instead. Returns the message to show.
 */
export function rememberSsoRefusal(code) {
  sessionStorage.setItem(SSO_PICK_ACCOUNT, "1");
  return ssoErrorMessage(code);
}

/** Whether this deployment offers SSO. Any failure means "no": the password form always works. */
export async function getSsoConfig() {
  try {
    const response = await fetch(`${BASE}/api/sso/config`, { credentials: "include" });
    if (!response.ok) return false;
    return (await response.json())?.enabled === true;
  } catch {
    return false;
  }
}

/** `{ url }` to send the browser to, or `{ message }` when SSO cannot be started. */
export async function startSso() {
  try {
    const response = await fetch(`${BASE}/api/sso/start`, { credentials: "include" });
    const body = await response.json().catch(() => ({}));
    const raw = body?.authorize_url || body?.data?.authorize_url;
    if (!response.ok || !raw) {
      return { message: body?.detail || body?.message || "Single sign-on is not available." };
    }
    const url = new URL(raw);
    if (sessionStorage.getItem(SSO_PICK_ACCOUNT) === "1") {
      sessionStorage.removeItem(SSO_PICK_ACCOUNT);
      // "login" makes Zitadel ask for credentials again even though it still holds the refused session.
      url.searchParams.set("prompt", "login");
    }
    return { url: url.toString() };
  } catch {
    return { message: "Single sign-on is not available." };
  }
}

/** Exchanges the proxy's session cookie for a Planner sign-in. Resolves the identity; throws an Error
 *  with a message fit to show when the person is turned away. */
export async function completeSso() {
  let response;
  try {
    response = await fetch(`${BASE}/api/sso/complete`, {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json" },
    });
  } catch {
    throw new Error("Single sign-on failed. Please try again.");
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data.access) {
    if (REFUSED_CODES.includes(data?.code)) sessionStorage.setItem(SSO_PICK_ACCOUNT, "1");
    throw new Error(data?.detail || "Single sign-on failed. Please try again.");
  }
  tokens.set({ access: data.access, refresh: data.refresh });
  localStorage.setItem(AUTH_MODE_KEY, AUTH_MODE_SSO);
  const { access: _a, refresh: _r, sso: _s, session_expires_at: _e, ...identity } = data;
  return identity;
}
